import React from 'react';

// Headings rendered like an old monitor: the text is drawn at low resolution
// onto a canvas, alternate rows are darkened (one bright row, one dark row), and
// the canvas is scaled up with nearest-neighbour sampling so both the glyphs and
// the scanlines land on a hard pixel grid.  The cap height is snapped to an odd
// number of rows so the letters start and end on a bright line.
//
// Props
//   text      heading text
//   rows      cap height in canvas pixels (odd keeps the lines balanced)
//   scale     CSS px per canvas px
//   as        element tag: 'h1' (default) or 'h2'
//   weight    font weight for the canvas text
//   wrap      wrap words to the container width (for long section headings)
//   glitch    add the red/cyan tear layers (contact page)
//   align     text-align of the wrapper
//   className extra class on the heading element
//   style     style on the wrapper

const DARK = 0.28;          // brightness of the dark rows
const CAP_HEIGHT = 0.706;   // Montserrat cap height in em (measured)

class CrtTitle extends React.Component {
  constructor(props) {
    super(props);
    this.hostRef = React.createRef();
    this.mainRef = React.createRef();
    this.layerARef = React.createRef();
    this.layerBRef = React.createRef();
    this.state = { width: 0, height: 0 };
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    const draw = () => this.draw();
    if (document.fonts && document.fonts.load) {
      document.fonts.load(`${this.props.weight || 'normal'} 20px main`).then(draw, draw);
    } else {
      draw();
    }
    // Redraw whenever the container's width changes (layout settling, window
    // resize, flex reflow) so the title re-wraps or shrinks to fit
    if (typeof ResizeObserver !== 'undefined' && this.hostRef.current) {
      this.observer = new ResizeObserver(this.handleResize);
      this.observer.observe(this.hostRef.current.parentElement);
    } else {
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.observer) this.observer.disconnect();
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
  }

  componentDidUpdate(prev) {
    if (prev.text !== this.props.text || prev.rows !== this.props.rows || prev.scale !== this.props.scale) {
      this.draw();
    }
  }

  handleResize() {
    const host = this.hostRef.current;
    const w = host && host.parentElement ? host.parentElement.clientWidth : 0;
    if (w === this.lastWidth) return;
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.draw());
  }

  draw(rowsOverride) {
    const { text, rows: rowsWanted = 49, scale = 1, weight = 'normal', wrap = false } = this.props;
    const rows = rowsOverride || rowsWanted;
    const pad = 2;

    // Pick the font size that makes the cap height exactly `rows` pixels
    const fontPx = rows / CAP_HEIGHT;
    const font = `${weight} ${fontPx}px main, sans-serif`;
    const measure = document.createElement('canvas').getContext('2d');
    measure.font = font;

    // Word-wrap to the container width when asked to
    let lines = [text];
    if (wrap && this.hostRef.current && this.hostRef.current.parentElement) {
      this.lastWidth = this.hostRef.current.parentElement.clientWidth;
      const maxW = this.lastWidth / scale - pad * 2;
      lines = [];
      let line = '';
      for (const word of text.split(' ')) {
        const candidate = line ? `${line} ${word}` : word;
        if (line && measure.measureText(candidate).width > maxW) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
      }
      if (line) lines.push(line);
    }

    // Line pitch is even so every line starts on a bright row
    let lineStep = rows + Math.max(3, Math.round(rows * 0.6));
    if (lineStep % 2) lineStep++;

    const widths = lines.map(l => measure.measureText(l).width);
    const descent = Math.max(0, ...lines.map(l => measure.measureText(l).actualBoundingBoxDescent || 0));
    const width = Math.ceil(Math.max(...widths)) + pad * 2;

    // Too wide for the container (narrow screens): re-render at fewer rows so
    // the title shrinks but stays on the pixel grid.  Keep rows odd for balance.
    const host = this.hostRef.current;
    const avail = host && host.parentElement ? host.parentElement.clientWidth : 0;
    if (!wrap && !rowsOverride && avail > 0 && width * scale > avail) {
      let fitRows = Math.floor(rows * avail / (width * scale));
      if (fitRows % 2 === 0) fitRows--;
      if (fitRows >= 9 && fitRows < rows) return this.draw(fitRows);
    }
    this.lastWidth = avail;
    const height = pad * 2 + (lines.length - 1) * lineStep + rows + Math.ceil(descent);

    const src = document.createElement('canvas');
    src.width = width;
    src.height = height;
    const ctx = src.getContext('2d');
    ctx.font = font;
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#ffffff';
    lines.forEach((l, k) => ctx.fillText(l, pad, pad + rows + k * lineStep));

    // Soften the horizontal edges: keep the font's anti-aliasing and run a
    // light 3-tap blur along x so stroke edges ramp instead of stepping.
    // Rows are left untouched so the scanlines stay hard.
    const img = ctx.getImageData(0, 0, width, height);
    const d = img.data;
    const alpha = new Float32Array(width * height);
    for (let i = 0; i < width * height; i++) alpha[i] = d[i * 4 + 3] / 255;
    for (let y = 0; y < height; y++) {
      const dark = ((y - pad) % 2 + 2) % 2 === 1;
      const v = dark ? Math.round(255 * DARK) : 255;
      for (let x = 0; x < width; x++) {
        const l = alpha[y * width + Math.max(0, x - 1)];
        const c = alpha[y * width + x];
        const r = alpha[y * width + Math.min(width - 1, x + 1)];
        let a = l * 0.25 + c * 0.5 + r * 0.25;
        // Gentle contrast so the core of each stroke stays solid
        a = Math.min(1, Math.max(0, (a - 0.08) / 0.84));
        const i = (y * width + x) * 4;
        d[i] = v; d[i + 1] = v; d[i + 2] = v;
        d[i + 3] = Math.round(a * 255);
      }
    }

    const paint = (ref, tint) => {
      const c = ref.current;
      if (!c) return;
      c.width = width;
      c.height = height;
      const cx = c.getContext('2d');
      cx.putImageData(img, 0, 0);
      if (tint) {
        cx.globalCompositeOperation = 'source-in';
        cx.fillStyle = tint;
        cx.fillRect(0, 0, width, height);
      }
    };
    paint(this.mainRef, null);
    paint(this.layerARef, '#ff3030');
    paint(this.layerBRef, '#30e8ff');

    if (width !== this.state.width || height !== this.state.height) {
      this.setState({ width, height });
    }
  }

  render() {
    const {
      text, scale = 1, as: Tag = 'h1', className = 'page-title',
      glitch = false, align = 'center', style = {}
    } = this.props;
    const { width, height } = this.state;
    const canvasStyle = { width: width * scale + 'px', height: height * scale + 'px' };
    return (
      <div style={{ textAlign: align, ...style }}>
        <Tag ref={this.hostRef} className={`${className} crt-title ${glitch ? 'crt-glitch' : ''}`}>
          <span className="visually-hidden">{text}</span>
          <canvas ref={this.mainRef} className="crt-main" style={canvasStyle} aria-hidden="true" />
          {glitch && <canvas ref={this.layerARef} className="crt-layer crt-layer-a" style={canvasStyle} aria-hidden="true" />}
          {glitch && <canvas ref={this.layerBRef} className="crt-layer crt-layer-b" style={canvasStyle} aria-hidden="true" />}
        </Tag>
      </div>
    );
  }
}


export default CrtTitle;
